import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { UserAvatar } from "@/components/UserAvatar";
import { ServiceLocationLabel } from "@/components/ServiceLocationLabel";
import { AvailabilityDisplay, WeeklySchedule } from "@/components/services/AvailabilitySchedule";
import { Computer, PenTool, FileText, TrendingUp, Gamepad2, GraduationCap, Camera, Music, Languages, HeartPulse, Shirt, ShoppingBag, Palette, Cog } from "lucide-react";

// Category icons map
const categoryIcons: Record<string, React.FC<{ className?: string }>> = {
  "Web Development": Computer,
  "Design": PenTool,
  "Writing": FileText,
  "Marketing and Sales": TrendingUp,
  "Game Development": Gamepad2,
  "Tutoring": GraduationCap,
  "Photography": Camera,
  "Music": Music,
  "Language": Languages,
  "Health": HeartPulse,
  "Fashion": Shirt,
  "Business": ShoppingBag,
  "Arts": Palette,
  "Engineering and CAD": Cog,
  "Other": Computer
};

// Category colors
const categoryColors: Record<string, string> = {
  "Web Development": "bg-blue-500 text-white",
  "Design": "bg-pink-500 text-white",
  "Writing": "bg-green-500 text-white",
  "Marketing and Sales": "bg-orange-500 text-white",
  "Game Development": "bg-purple-500 text-white",
  "Tutoring": "bg-amber-500 text-white",
  "Photography": "bg-indigo-500 text-white",
  "Music": "bg-red-500 text-white",
  "Language": "bg-emerald-500 text-white",
  "Health": "bg-rose-500 text-white",
  "Fashion": "bg-fuchsia-500 text-white",
  "Business": "bg-cyan-500 text-white",
  "Arts": "bg-violet-500 text-white",
  "Engineering and CAD": "bg-slate-500 text-white",
  "Other": "bg-gray-500 text-white"
};

interface ServiceDescriptionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description: string;
  imageUrl?: string | null;
  categoryName?: string;
  userProfile?: {
    id: string;
    name: string;
    avatarInitials: string;
    avatarUrl?: string;
  };
  price?: number;
  pricingType?: string;
  deliveryMethod?: string | null;
  locationName?: string;
  availabilitySchedule?: any;
}

export const ServiceDescriptionDialog: React.FC<ServiceDescriptionDialogProps> = ({
  isOpen,
  onClose,
  title,
  description,
  imageUrl,
  categoryName = "Other",
  userProfile,
  price,
  pricingType,
  deliveryMethod,
  locationName,
  availabilitySchedule,
}) => {
  const IconComponent = categoryIcons[categoryName] || Computer;
  const colorClass = categoryColors[categoryName] || "bg-gray-500 text-white";

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-0">
        {/* Image Header */}
        <div className="aspect-[4/3] bg-gray-100 dark:bg-gray-700 relative overflow-hidden rounded-t-lg">
          {imageUrl ? (
            <>
              <img 
                src={imageUrl} 
                alt={title} 
                className="w-full h-full object-contain bg-gray-50 dark:bg-gray-800" 
              />
              <div className="absolute top-3 right-3">
                <div className={`${colorClass} p-2 rounded-full shadow-lg border-2 border-white backdrop-blur-sm bg-opacity-90`}>
                  <IconComponent className="h-5 w-5" />
                </div>
              </div>
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className={`${colorClass} p-4 rounded-full`}>
                <IconComponent className="h-10 w-10" />
              </div>
            </div>
          )}
        </div>

        <div className="p-4">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">{title}</DialogTitle>
          </DialogHeader>

          {/* User Profile */}
          {userProfile && (
            <div className="flex items-center mt-3 mb-4">
              <UserAvatar
                userId={userProfile.id}
                avatarUrl={userProfile.avatarUrl}
                fallback={userProfile.avatarInitials}
                size="sm"
                showLevel={true}
                clickable={true}
                className="mr-2"
              />
              <span className="text-sm text-muted-foreground">{userProfile.name}</span>
            </div>
          )}

          {/* Description */}
          <div className="mb-4">
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
              {description || "No description provided."}
            </p>
          </div>

          {/* Location & Availability */}
          {deliveryMethod && (
            <ServiceLocationLabel 
              deliveryMethod={deliveryMethod as "virtual" | "in-person" | "both"} 
              location={locationName} 
              className="mb-2 text-xs" 
            />
          )}
          
          {availabilitySchedule && deliveryMethod !== "virtual" && (
            <div className="mb-4">
              <AvailabilityDisplay schedule={availabilitySchedule as WeeklySchedule} />
            </div>
          )}

          {/* Price */}
          {price !== undefined && (
            <div className="flex items-center pt-3 border-t border-border">
              <span className="font-semibold text-lg">${price}</span>
              {pricingType === 'hourly' && (
                <span className="text-sm text-muted-foreground ml-1">/hr</span>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
